import { type CSSObject, keyframes } from '@emotion/react';
import { useTheme } from '../../../theme';

type Props = {
  disableAnimation?: boolean;
  isOpen?: boolean;
};

type Return = Record<'container' | 'title', CSSObject>;

const menuIn = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.75);
    filter: blur(8px);
  }
  100% {
    opacity: 1;
    transform: scale(1);
    filter: blur(0);
  }
`;

const menuOut = keyframes`
  0% {
    opacity: 1;
    transform: scale(1);
    filter: blur(0);
  }
  100% {
    opacity: 0;
    transform: scale(0.75);
    filter: blur(8px);
  }
`;

export const useContextMenuStyles = ({
  disableAnimation,
  isOpen = true,
}: Props): Return => {
  const theme = useTheme();

  return {
    container: {
      display: 'flex',
      minWidth: 220,
      padding: theme.spacing(2, 0),
      flexDirection: 'column',
      alignItems: 'stretch',
      borderRadius: theme.shape.radius12,
      border: `1px solid ${theme.menu.background.border}`,
      backgroundColor: theme.menu.background.default,
      boxShadow: theme.shadows[1],
      transformOrigin: 'top left',

      '[data-popper-placement^="bottom"] &': {
        transformOrigin: 'top center',
      },
      '[data-popper-placement="bottom-start"] &': {
        transformOrigin: 'top left',
      },
      '[data-popper-placement="bottom-end"] &': {
        transformOrigin: 'top right',
      },

      '[data-popper-placement^="top"] &': {
        transformOrigin: 'bottom center',
      },
      '[data-popper-placement="top-start"] &': {
        transformOrigin: 'bottom left',
      },
      '[data-popper-placement="top-end"] &': {
        transformOrigin: 'bottom right',
      },

      '[data-popper-placement^="left"] &': {
        transformOrigin: 'center right',
      },
      '[data-popper-placement="left-start"] &': {
        transformOrigin: 'top right',
      },
      '[data-popper-placement="left-end"] &': {
        transformOrigin: 'bottom right',
      },

      '[data-popper-placement^="right"] &': {
        transformOrigin: 'center left',
      },
      '[data-popper-placement="right-start"] &': {
        transformOrigin: 'top left',
      },
      '[data-popper-placement="right-end"] &': {
        transformOrigin: 'bottom left',
      },

      animation: disableAnimation
        ? 'none'
        : isOpen
        ? `${menuIn} 180ms cubic-bezier(0.16, 1, 0.3, 1) forwards`
        : `${menuOut} 140ms cubic-bezier(0.4, 0, 1, 1) forwards`,
      '@media (prefers-reduced-motion: reduce)': {
        animation: 'none',
      },
    },
    title: {
      alignSelf: 'start',
      userSelect: 'none',
      color: theme.text.generic.primary,
      marginBottom: theme.spacing(2),
    },
  };
};
